import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScheduleFirstLessonComponent, ScheduleFirstLessonData, ScheduleFirstLessonSubmitData } from '../../inquiry/schedule-first-lesson/schedule-first-lesson.component';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { ReplaySubject } from 'rxjs';
import moment from 'moment';
import { LoggerService } from '../../../services/logger.service';

export interface BookFromInquiryData {
  inquiryId: number;
  studentName?: string;
  inquiryDate?: string;
  studentId?: number;
  contactId?: number;
}

@Component({
  selector: 'app-book-from-inquiry-report',
  templateUrl: './book-from-inquiry-report.component.html',
  styleUrls: ['./book-from-inquiry-report.component.css']
})
export class BookFromInquiryReportComponent implements OnInit {
  @ViewChild(ScheduleFirstLessonComponent) scheduleFirstLessonComponent: ScheduleFirstLessonComponent;
  @Input() showScheduleToggle: boolean = false;

  // Data for the schedule-first-lesson component
  scheduleData: ScheduleFirstLessonData = {
    enabled: true
  };

  // Only allow Pre Original and Dance Evaluation lesson types
  allowedLessonTypeNames: string[] = ['Pre Original', 'Dance Evaluation (DE)', 'Pre Original (Couple)'];

  // Track whether there are scheduling conflicts
  hasConflicts: boolean = false;
  showProgressSpinner: boolean = false;
  errorMessage: string = '';

  // Display name(s) for all contacts on the student account
  displayStudentName: string = '';
  isCouple: boolean = false;

  // Variables for package handling
  private DEPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalCouplePackageSubject = new ReplaySubject<any>(1);

  // Public observables to expose the subjects
  public DEPackage$ = this.DEPackageSubject.asObservable();
  public PreOriginalPackage$ = this.PreOriginalPackageSubject.asObservable();
  public PreOriginalCouplePackage$ = this.PreOriginalCouplePackageSubject.asObservable();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BookFromInquiryData,
    private dialogRef: MatDialogRef<BookFromInquiryReportComponent>,
    private _drupalRESTService: DrupalRESTService,
    private _entityRESTService: EntityRESTService,
    private _authService: AuthService
  ) { }

  ngOnInit(): void {
    // Set initial display name from inquiry data (single contact name)
    this.displayStudentName = this.data.studentName || '';

    // Load all contacts from the student account for couple detection
    this.loadStudentAccountContacts();

    // Initialize schedule data with inquiry information
    this.scheduleData = {
      ...this.scheduleData,
      inquiryId: this.data.inquiryId,
      studentName: this.data.studentName,
      inquiryDate: this.data.inquiryDate,
      studentId: this.data.studentId,
      enabled: true,
      dateTime: moment().add(1, 'day').hour(10).minute(0).second(0).toDate(), // Default to 10am tomorrow
      showScheduleToggle: this.showScheduleToggle // Pass the showScheduleToggle flag to the child component
    };
  }

  /**
   * Load the student account entity to get all contacts.
   * If the account has multiple contacts (couple), display all names.
   */
  private loadStudentAccountContacts(): void {
    const studentId = this.data.studentId;
    if (!studentId) {
      return;
    }

    this._entityRESTService.getEntity('student_accounts', 'student_account', studentId).subscribe(
      (studentAccount: any) => {
        if (studentAccount && studentAccount.field_contacts) {
          const contacts = Array.isArray(studentAccount.field_contacts)
            ? studentAccount.field_contacts
            : Object.values(studentAccount.field_contacts);

          // Filter out archived contacts and build name list
          const activeContacts = contacts.filter((c: any) => c && c.field_first_name && c.field_archived !== '1' && c.field_archived !== 1);

          if (activeContacts.length > 1) {
            this.isCouple = true;
            const names = activeContacts.map((c: any) =>
              `${c.field_first_name} ${c.field_last_name}`.trim()
            );
            this.displayStudentName = names.join(' & ');
          } else if (activeContacts.length === 1) {
            this.displayStudentName = `${activeContacts[0].field_first_name} ${activeContacts[0].field_last_name}`.trim();
          }
        }
      },
      (error) => {
        LoggerService.error('Error loading student account contacts:', error);
        // Keep the original single name from inquiry data as fallback
      }
    );
  }

  /**
   * Extract contact IDs from a student account's field_contacts.
   * Handles both collapsed entities (with `id`) and raw references (with `target_id`),
   * and the single-value unwrapping the backend does when there's only one contact.
   */
  private extractContactIds(fieldContacts: any): number[] {
    if (!fieldContacts) return [];

    // Normalize to array: backend unwraps single-element arrays to a plain object
    const contactsArray = Array.isArray(fieldContacts) ? fieldContacts : [fieldContacts];

    const ids: number[] = [];
    for (const contact of contactsArray) {
      if (!contact) continue;
      // Collapsed entities have `id`, raw references have `target_id`
      const id = contact.id || contact.target_id;
      if (id) {
        ids.push(Number(id));
      }
    }
    return ids;
  }

  /**
   * Handle schedule conflict events from the child component
   */
  onConflictFound(hasConflict: boolean): void {
    this.hasConflicts = hasConflict;
  }

  /**
   * Check if the form is valid for submission
   */
  isFormValid(): boolean {
    return this.scheduleData.enabled &&
           !!this.scheduleData.executive &&
           !!this.scheduleData.teacher &&
           !!this.scheduleData.lessonType &&
           !!this.scheduleData.dateTime;
  }

  /**
   * Handle schedule submission from child component
   */
  onScheduleSubmit(submittedData: ScheduleFirstLessonSubmitData): void {
    this.onSave();
  }

  /**
   * Fetch system packages needed for special enrollments
   */
  async pullSystemPackages(): Promise<void> {
    // Pull the system packages from the /packages endpoint.
    let endpoint: string = "/api/v1/packages";
    let params = [
      { parameter: "field_retired_op", value: "=" },
      { parameter: "field_retired_value", value: "0" },
      { parameter: "field_is_system_package_op", value: "=" },
      { parameter: "field_is_system_package_value", value: "1" },
    ];

    return new Promise<void>((resolve, reject) => {
      this._drupalRESTService.httpGET(endpoint, params).subscribe(
        (data) => {
          let DE_package = null;
          let PreOriginal_package = null;
          let PreOriginalCouple_package = null;

          // Several system packages can share an SPS code + lesson type (the
          // Global Guest package is PORI + Pre Original like Pre-Original), and
          // studios rename them freely, so the oldest match is the seeded one.
          const systemPackages = [...(data?.['results'] ?? [])]
            .sort((a, b) => Number(a.id) - Number(b.id));

          systemPackages.forEach(packageData => {
            if (!DE_package && packageData.field_sps_code === 'DE') {
              DE_package = packageData;
            }
            if (!PreOriginal_package && packageData.field_sps_code === 'PORI' && packageData.field_lesson_type === 'Pre Original') {
              PreOriginal_package = packageData;
            }
            if (!PreOriginalCouple_package && packageData.field_sps_code === 'PORI' && packageData.field_lesson_type === 'Pre Original (Couple)') {
              PreOriginalCouple_package = packageData;
            }
          });

          LoggerService.log('DE package in pullSystemPackages:', DE_package);
          LoggerService.log('PreOriginal package in pullSystemPackages:', PreOriginal_package);
          LoggerService.log('PreOriginalCouple package in pullSystemPackages:', PreOriginalCouple_package);

          // Emit the packages through the subjects
          this.DEPackageSubject.next(DE_package);
          this.PreOriginalPackageSubject.next(PreOriginal_package);
          this.PreOriginalCouplePackageSubject.next(PreOriginalCouple_package);

          resolve();
        },
        (error) => {
          LoggerService.error('Error pulling system packages:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Create a Dance Evaluation enrollment
   */
  async createDEEnrollment(studentData: any, scheduledDateTime: string, DEPackage: any): Promise<any> {
    try {
      // Load the full package details
      const dePackage = await this._entityRESTService.getEntity('packages', 'package', DEPackage?.id).toPromise();

      LoggerService.log('Student data for DE enrollment:', studentData);

      let studentContactIds: number[] = [];

      // Try to get student account data for contacts
      try {
        if (studentData && studentData.id) {
          const studentAccountData: any = await this._entityRESTService.getEntity('student_accounts', 'student_account', studentData.id).toPromise();
          LoggerService.log('Student account data:', studentAccountData);
          studentContactIds = this.extractContactIds(studentAccountData?.field_contacts);
          LoggerService.log('Extracted student contact IDs:', studentContactIds);
        }
      } catch (error) {
        LoggerService.error('Error fetching student account data:', error);
      }

      // Fallback to contactId if no contacts found
      if (studentContactIds.length === 0 && this.data.contactId) {
        LoggerService.log('Using contactId as fallback:', this.data.contactId);
        studentContactIds.push(this.data.contactId);
      }

      // Read package properties properly
      const lessonPrice = parseFloat(dePackage?.['field_lesson_price']) || 0;
      const lessonCount = parseInt(dePackage?.['field_lesson_count']) || 1;
      const discountType = dePackage?.['field_discount_type'] || 'flat';
      const discountValue = parseFloat(dePackage?.['field_discount']) || 0;

      // Load the studio's tax rate from configuration
      let taxRate = 0;
      try {
        const taxConfig = await this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').toPromise();
        if (taxConfig && Array.isArray(taxConfig) && taxConfig.length > 0) {
          const currentDate = moment();
          // Find applicable taxes
          taxConfig.forEach(tax => {
            const startDate = tax.field_effective_tax_start_date?.[0]?.value
              ? moment(tax.field_effective_tax_start_date[0].value)
              : null;
            const endDate = tax.field_effective_tax_end_date?.[0]?.value
              ? moment(tax.field_effective_tax_end_date[0].value)
              : null;

            // Only include taxes that are currently effective
            if ((!startDate || currentDate.isSameOrAfter(startDate)) &&
                (!endDate || currentDate.isSameOrBefore(endDate))) {
              const calculationMethod = Array.isArray(tax.field_calculation_method)
                ? tax.field_calculation_method[0]?.value
                : tax.field_calculation_method;

              const taxValue = Array.isArray(tax.field_tax_value)
                ? parseFloat(tax.field_tax_value[0]?.value)
                : parseFloat(tax.field_tax_value);

              if (calculationMethod === 'PERCENTAGE' && !isNaN(taxValue)) {
                taxRate += taxValue;
              }
            }
          });
        }
      } catch (error) {
        LoggerService.error('Error loading tax rate:', error);
      }

      // Calculate total lessons price without discount
      const totalLessonsPrice = lessonPrice * lessonCount;

      // Calculate discount
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = totalLessonsPrice * (discountValue / 100);
      } else { // 'flat'
        discountAmount = discountValue;
      }

      // Calculate price after discount
      const priceAfterDiscount = totalLessonsPrice - discountAmount;

      // Calculate tax amount
      const taxAmount = priceAfterDiscount * (taxRate / 100);

      // Total with tax
      const totalWithTax = priceAfterDiscount + taxAmount;

      LoggerService.log('DE Package:', dePackage);
      LoggerService.log('DE Package Price:', lessonPrice);
      LoggerService.log('Discount:', discountAmount);
      LoggerService.log('Tax:', taxAmount);

      let enrollmentBody = {
        "field_draft": false,
        "field_student": studentData.id,
        "field_sale_date": moment(scheduledDateTime).format('YYYY-MM-DDTHH:mm:ss'),
        "field_expiration_date": moment(scheduledDateTime).add(4, 'weeks').format('YYYY-MM-DD'),
        "field_category": "1395", // DE category
        "field_enrollment_package_name": dePackage?.['id'],
        "field_enrollment_lesson_price": lessonPrice,
        "field_enrollment_lesson_count": lessonCount,
        "field_total_lessons_price": totalLessonsPrice,
        "field_enrollment_total_price": priceAfterDiscount,
        "field_total_price_with_est_tax": totalWithTax,
        "field_taxes": taxAmount,
        "field_tax_percentage": taxRate,
        "field_total_paid": null,
        "field_lesson_used": null,
        "field_lessons_paid": null,
        "field_lesson_available": null,
        "field_payments_structure": "paid_in_full",
        "field_scheduled_payments": [
          {
            "field_amount_paid": null,
            "field_enrollment": null,
            "field_paid_in_full": true,
            "field_down_payment": true,
            "field_payment_amount": priceAfterDiscount,
            "field_tax_amount": taxAmount,
            "field_payment_date": moment(scheduledDateTime).format("YYYY-MM-DD"),
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "scheduled_payments",
            "type": "scheduled_payments"
          }
        ],
        "field_instructor_percentages": [
          {
            "field_amount": priceAfterDiscount,
            "field_instructor": this.scheduleData.teacher,
            "field_percentage": 100,
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "instructor_percentages",
            "type": "instructor_percentages"
          }
        ],
        "field_interval_unit": "",
        "field_discount": discountValue,
        "field_discount_type": discountType,
        "field_enrollment_status": "Open",
        "field_next_scheduled_payment": "",
        "field_visibility": false,
        "field_notes": "Booked from inquiry report"
      };

      LoggerService.log('Enrollment Body:', enrollmentBody);

      // Create the enrollment for the student
      const enrollmentData = await this._entityRESTService.createEntity('packages', 'enrollment', enrollmentBody).toPromise();
      LoggerService.log('DE Enrollment created:', enrollmentData);

      // Skip creating events if no student contacts were found
      if (studentContactIds.length === 0) {
        LoggerService.warn('No student contacts found for the account. Not creating lesson/service events.');
        return enrollmentData;
      }

      // Now create the lesson event
      let eventBody = {
        "field_type": this.scheduleData.lessonType,
        "field_instructor": { target_id: this.scheduleData.teacher },
        "field_duration": this.getLessonDuration(this.scheduleData.lessonType),
        "field_date_and_time": scheduledDateTime,
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_status": "64",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0],
            "field_enrollment": { target_id: enrollmentData['id'][0]['value'] }
          }
        ]
      };

      LoggerService.log('Creating DE Lesson event:', eventBody);
      const eventData = await this._entityRESTService.createEntity('events', 'lesson', eventBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('DE Lesson event created:', eventData);

      // Create the DE Pre Chat service
      let DEServiceBody = {
        'field_type': 1404,
        'field_status': 106,
        'field_executive': this.scheduleData.executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(scheduledDateTime).subtract(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0]
          }
        ]
      };

      LoggerService.log('Creating DE Pre Chat service:', DEServiceBody);
      const DEServiceData = await this._entityRESTService.createEntity('events', 'services', DEServiceBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('DE Pre Chat service created:', DEServiceData);

      // Create the Original Chat service
      let OriginalChatServiceBody = {
        'field_type': 567,
        'field_status': 113,
        'field_executive': this.scheduleData.executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(scheduledDateTime).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0]
          }
        ]
      };

      LoggerService.log('Creating Original Chat service:', OriginalChatServiceBody);
      const OriginalChatServiceData = await this._entityRESTService.createEntity('events', 'services', OriginalChatServiceBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('Original Chat service created:', OriginalChatServiceData);

      return enrollmentData;
    } catch (error) {
      LoggerService.error('Error creating DE enrollment:', error);
      throw error;
    }
  }

  /**
   * Create a Pre-Original Program enrollment
   */
  async createPreOriginalEnrollment(studentData: any, scheduledDateTime: string, PreOriginalPackage: any): Promise<any> {
    try {
      // Load the full package details
      const preOriginalPackage = await this._entityRESTService.getEntity('packages', 'package', PreOriginalPackage?.id).toPromise();

      LoggerService.log('Student data for Pre-Original enrollment:', studentData);

      let studentContactIds: number[] = [];

      // Try to get student account data for contacts
      try {
        if (studentData && studentData.id) {
          const studentAccountData: any = await this._entityRESTService.getEntity('student_accounts', 'student_account', studentData.id).toPromise();
          LoggerService.log('Student account data:', studentAccountData);
          studentContactIds = this.extractContactIds(studentAccountData?.field_contacts);
          LoggerService.log('Extracted student contact IDs:', studentContactIds);
        }
      } catch (error) {
        LoggerService.error('Error fetching student account data:', error);
      }

      // Fallback to contactId if no contacts found
      if (studentContactIds.length === 0 && this.data.contactId) {
        LoggerService.log('Using contactId as fallback:', this.data.contactId);
        studentContactIds.push(this.data.contactId);
      }

      // Read package properties properly
      const lessonPrice = parseFloat(preOriginalPackage?.['field_lesson_price']) || 25;
      const lessonCount = parseInt(preOriginalPackage?.['field_lesson_count']) || 1;
      const discountType = preOriginalPackage?.['field_discount_type'] || 'flat';
      const discountValue = parseFloat(preOriginalPackage?.['field_discount']) || 0;

      // Load the studio's tax rate from configuration
      let taxRate = 0;
      try {
        const taxConfig = await this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').toPromise();
        if (taxConfig && Array.isArray(taxConfig) && taxConfig.length > 0) {
          const currentDate = moment();
          // Find applicable taxes
          taxConfig.forEach(tax => {
            const startDate = tax.field_effective_tax_start_date?.[0]?.value
              ? moment(tax.field_effective_tax_start_date[0].value)
              : null;
            const endDate = tax.field_effective_tax_end_date?.[0]?.value
              ? moment(tax.field_effective_tax_end_date[0].value)
              : null;

            // Only include taxes that are currently effective
            if ((!startDate || currentDate.isSameOrAfter(startDate)) &&
                (!endDate || currentDate.isSameOrBefore(endDate))) {
              const calculationMethod = Array.isArray(tax.field_calculation_method)
                ? tax.field_calculation_method[0]?.value
                : tax.field_calculation_method;

              const taxValue = Array.isArray(tax.field_tax_value)
                ? parseFloat(tax.field_tax_value[0]?.value)
                : parseFloat(tax.field_tax_value);

              if (calculationMethod === 'PERCENTAGE' && !isNaN(taxValue)) {
                taxRate += taxValue;
              }
            }
          });
        }
      } catch (error) {
        LoggerService.error('Error loading tax rate:', error);
      }

      // Calculate total lessons price without discount
      const totalLessonsPrice = lessonPrice * lessonCount;

      // Calculate discount
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = totalLessonsPrice * (discountValue / 100);
      } else { // 'flat'
        discountAmount = discountValue;
      }

      // Calculate price after discount
      const priceAfterDiscount = totalLessonsPrice - discountAmount;

      // Calculate tax amount
      const taxAmount = priceAfterDiscount * (taxRate / 100);

      // Total with tax
      const totalWithTax = priceAfterDiscount + taxAmount;

      let enrollmentBody = {
        "field_draft": false,
        "field_student": studentData.id,
        "field_sale_date": moment(scheduledDateTime).format("YYYY-MM-DDTHH:mm:ss"),
        "field_expiration_date": moment(scheduledDateTime).add(4, 'weeks').format('YYYY-MM-DD'),
        "field_category": "49", // Pre-Original category
        "field_enrollment_package_name": preOriginalPackage?.['id'],
        "field_enrollment_lesson_price": lessonPrice,
        "field_enrollment_lesson_count": lessonCount,
        "field_total_lessons_price": totalLessonsPrice,
        "field_enrollment_total_price": priceAfterDiscount,
        "field_total_price_with_est_tax": totalWithTax,
        "field_taxes": taxAmount,
        "field_tax_percentage": taxRate,
        "field_total_paid": null,
        "field_lesson_used": null,
        "field_lessons_paid": null,
        "field_lesson_available": null,
        "field_payments_structure": "paid_in_full",
        "field_scheduled_payments": [
          {
            "field_amount_paid": null,
            "field_enrollment": null,
            "field_paid_in_full": true,
            "field_down_payment": true,
            "field_payment_amount": priceAfterDiscount,
            "field_tax_amount": taxAmount,
            "field_payment_date": moment(scheduledDateTime).format("YYYY-MM-DD"),
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "scheduled_payments",
            "type": "scheduled_payments"
          }
        ],
        "field_instructor_percentages": [
          {
            "field_amount": priceAfterDiscount,
            "field_instructor": this.scheduleData.teacher,
            "field_percentage": 100,
            "field_studio_reference": this._authService.selectedStudio?.id,
            "bundle": "instructor_percentages",
            "type": "instructor_percentages"
          }
        ],
        "field_interval_unit": "",
        "field_discount": discountValue,
        "field_discount_type": discountType,
        "field_enrollment_status": "Open",
        "field_next_scheduled_payment": "",
        "field_visibility": false,
        "field_notes": "Booked from inquiry report"
      };

      LoggerService.log('Pre-Original Enrollment Body:', enrollmentBody);

      // Create the enrollment for the student
      const enrollmentData = await this._entityRESTService.createEntity('packages', 'enrollment', enrollmentBody).toPromise();
      LoggerService.log('Pre-Original Enrollment created:', enrollmentData);

      // Skip creating events if no student contacts were found
      if (studentContactIds.length === 0) {
        LoggerService.warn('No student contacts found for the account. Not creating lesson/service events.');
        return enrollmentData;
      }

      // Now create the lesson event
      let eventBody = {
        "field_type": this.scheduleData.lessonType,
        "field_instructor": { target_id: this.scheduleData.teacher },
        "field_duration": this.getLessonDuration(this.scheduleData.lessonType),
        "field_date_and_time": scheduledDateTime,
        "field_student": [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_status": "64",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0],
            "field_enrollment": { target_id: enrollmentData['id'][0]['value'] }
          }
        ]
      };

      LoggerService.log('Creating Pre-Original Lesson event:', eventBody);
      const eventData = await this._entityRESTService.createEntity('events', 'lesson', eventBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('Pre-Original Lesson event created:', eventData);

      // Create the Pre-Original Pre Chat service
      let PREServiceBody = {
        'field_type': 656,
        'field_status': 106,
        'field_executive': this.scheduleData.executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(scheduledDateTime).subtract(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0]
          }
        ]
      };

      LoggerService.log('Creating Pre-Original Pre Chat service:', PREServiceBody);
      const PREServiceData = await this._entityRESTService.createEntity('events', 'services', PREServiceBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('Pre-Original Pre Chat service created:', PREServiceData);

      // Create the Original Chat service
      let OriginalChatServiceBody = {
        'field_type': 567,
        'field_status': 113,
        'field_executive': this.scheduleData.executive,
        'field_duration': '00:15',
        'field_date_and_time': moment(scheduledDateTime).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
        'field_student': [
          {
            "type": "attendees",
            "bundle": "attendance",
            "field_student_account": studentData.id,
            "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0]
          }
        ]
      };

      LoggerService.log('Creating Original Chat service:', OriginalChatServiceBody);
      const OriginalChatServiceData = await this._entityRESTService.createEntity('events', 'services', OriginalChatServiceBody, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
      LoggerService.log('Original Chat service created:', OriginalChatServiceData);

      return enrollmentData;
    } catch (error) {
      LoggerService.error('Error creating Pre-Original enrollment:', error);
      throw error;
    }
  }

  /**
   * Get the default duration for a lesson type
   */
  getLessonDuration(lessonTypeId: string): string {
    // All lesson events should be 30 minutes
    return "00:30";
  }

  /**
   * Save the booking and create necessary records
   */
  async onSave(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    this.showProgressSpinner = true;
    this.errorMessage = '';

    try {
      // Format the date and time for submission
      const formattedDateTime = moment(this.scheduleData.dateTime).format('YYYY-MM-DDTHH:mm:ss');

      // Check if this is a special enrollment type
      const create_de_enrollment = Boolean(this.scheduleData.enabled && this.scheduleData.lessonType === '560');
      const create_preoriginal_enrollment = Boolean(this.scheduleData.enabled && this.scheduleData.lessonType === '1393');
      const create_preoriginal_couple_enrollment = Boolean(this.scheduleData.enabled && this.scheduleData.lessonType === '1429');

      // Fetch packages if needed for special enrollments
      if (create_de_enrollment || create_preoriginal_enrollment || create_preoriginal_couple_enrollment) {
        await this.pullSystemPackages();
      }

      // Log all available student data for debugging
      LoggerService.log('Complete data from inquiry:', this.data);
      LoggerService.log('Student ID from inquiry:', this.data.studentId);
      LoggerService.log('Contact ID from inquiry:', this.data.contactId);

      // Check if we have either a student ID or contact ID
      if (!this.data.studentId && !this.data.contactId) {
        this.errorMessage = 'Missing student ID. Cannot proceed with booking.';
        this.showProgressSpinner = false;
        return;
      }

      // Determine the appropriate ID to use
      const studentIdToUse = this.data.studentId || this.data.contactId;
      LoggerService.log('Using ID for enrollment:', studentIdToUse);

      // The booked_on field is updated automatically
      LoggerService.log('Proceeding with booking');

      // Create the appropriate enrollment based on lesson type
      if (create_de_enrollment) {
        LoggerService.log('Creating DE enrollment');
        try {
          await new Promise<void>((resolve, reject) => {
            this.DEPackage$.subscribe(
              async (DE_package) => {
                if (DE_package) {
                  try {
                    const studentData = { id: studentIdToUse };
                    await this.createDEEnrollment(studentData, formattedDateTime, DE_package);
                    LoggerService.log('DE enrollment created');
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating DE enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('DE package not found');
                  this.errorMessage = 'Dance Evaluation package not found';
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving DE package:', error);
                reject(error);
              }
            );
          });
        } catch (error) {
          LoggerService.error('Error creating DE enrollment:', error);
          this.errorMessage = 'Error creating Dance Evaluation enrollment';
          throw error;
        }
      } else if (create_preoriginal_enrollment) {
        LoggerService.log('Creating pre-original enrollment');
        try {
          await new Promise<void>((resolve, reject) => {
            this.PreOriginalPackage$.subscribe(
              async (PreOriginal_package) => {
                if (PreOriginal_package) {
                  try {
                    const studentData = { id: studentIdToUse };
                    await this.createPreOriginalEnrollment(studentData, formattedDateTime, PreOriginal_package);
                    LoggerService.log('Pre-original enrollment created');
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating pre-original enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('Pre-original package not found');
                  this.errorMessage = 'Pre-Original package not found';
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving pre-original package:', error);
                reject(error);
              }
            );
          });
        } catch (error) {
          LoggerService.error('Error creating pre-original enrollment:', error);
          this.errorMessage = 'Error creating Pre-Original enrollment';
          throw error;
        }
      } else if (create_preoriginal_couple_enrollment) {
        LoggerService.log('Creating pre-original couple enrollment');
        try {
          await new Promise<void>((resolve, reject) => {
            this.PreOriginalCouplePackage$.subscribe(
              async (PreOriginalCouple_package) => {
                if (PreOriginalCouple_package) {
                  try {
                    const studentData = { id: studentIdToUse };
                    await this.createPreOriginalEnrollment(studentData, formattedDateTime, PreOriginalCouple_package);
                    LoggerService.log('Pre-original couple enrollment created');
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating pre-original couple enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('Pre-original couple package not found');
                  this.errorMessage = 'Pre-Original couple package not found';
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving pre-original couple package:', error);
                reject(error);
              }
            );
          });
        } catch (error) {
          LoggerService.error('Error creating pre-original couple enrollment:', error);
          this.errorMessage = 'Error creating Pre-Original couple enrollment';
          throw error;
        }
      } else {
        // For regular lesson types that aren't DE or Pre-Original
        try {
          await this.scheduleFirstLesson();
        } catch (error) {
          LoggerService.error('Error scheduling first lesson:', error);
          this.errorMessage = 'Error scheduling first lesson';
          throw error;
        }
      }

      // Close the dialog with success
      this.dialogRef.close(true);
    } catch (error) {
      LoggerService.error('Error booking student:', error);
      if (!this.errorMessage) {
        this.errorMessage = 'An error occurred while booking the student';
      }
      // Don't close dialog on error
    } finally {
      this.showProgressSpinner = false;
    }
  }

  /**
   * Schedule the first lesson based on the selected options
   */
  private async scheduleFirstLesson(): Promise<any> {
    // Determine the appropriate ID to use
    const studentIdToUse = this.data.studentId || this.data.contactId;

    // Fetch all contacts from the student account
    let studentContactIds: number[] = [];
    try {
      if (studentIdToUse) {
        LoggerService.log('Fetching student account for contacts, ID:', studentIdToUse);
        const studentAccountData: any = await this._entityRESTService.getEntity('student_accounts', 'student_account', studentIdToUse).toPromise();
        LoggerService.log('Student account data received:', studentAccountData);
        studentContactIds = this.extractContactIds(studentAccountData?.field_contacts);
        LoggerService.log('Extracted contact IDs:', studentContactIds);
      }
    } catch (error) {
      LoggerService.error('Error fetching student account for contacts:', error);
    }

    // Fallback to contactId if no contacts found
    if (studentContactIds.length === 0 && this.data.contactId) {
      LoggerService.log('Using contactId as fallback:', this.data.contactId);
      studentContactIds.push(this.data.contactId);
    }

    // Create standard lesson/event
    const lessonData = {
      field_student: [
        {
          "type": "attendees",
          "bundle": "attendance",
          "field_status": "64",
          "field_student_account": studentIdToUse,
          "field_students": studentContactIds.length > 1 ? studentContactIds : studentContactIds[0]
        }
      ],
      field_type: this.scheduleData.lessonType,
      field_instructor: { target_id: this.scheduleData.teacher },
      field_executive: this.scheduleData.executive,
      field_date_and_time: moment(this.scheduleData.dateTime).format('YYYY-MM-DDTHH:mm:ss'),
      field_duration: this.getLessonDuration(this.scheduleData.lessonType),
      field_note: "Booked from inquiry report",
      field_studio_reference: this._authService.selectedStudio?.id,
    };

    try {
      // Create the lesson event
      LoggerService.log('Creating standard lesson event:', lessonData);
      return await this._entityRESTService.createEntity('events', 'lesson', lessonData, [{ 'parameter': '_format', 'value': 'json' }]).toPromise();
    } catch (error) {
      LoggerService.error('Error creating lesson:', error);
      throw error;
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  /**
   * Handle successful booking from the schedule component
   */
  onSuccessfulBooking(): void {
    this.dialogRef.close(true);
  }

  /**
   * Handle booking error from the schedule component
   */
  onBookingError(error: any): void {
    this.errorMessage = error?.message || 'An error occurred during booking';
  }
}
